import { useState } from 'react';
import { Lock, CheckCircle, Building2 } from 'lucide-react';
import { Project, AMIDataPoint, Equipment } from '../types/project';
import { PaymentStatus } from '../hooks/usePaymentStatus';
import { ChargeConfirmModal } from './ChargeConfirmModal';
import { BillingConsentModal } from './BillingConsentModal';
import { supabase } from '../lib/supabase';
import { lockScreenReadiness, LockScreenReadiness } from '../lib/lockScreenReadiness';

interface ResultsLockScreenProps {
  project: Partial<Project>;
  amiData: AMIDataPoint[];
  /** Full equipment list. The dwelling inventory (load_role 'existing') drives
   * the traditional readiness line; absent on older callers. */
  equipment?: Equipment[];
  projectId: string | null;
  paymentStatus: PaymentStatus;
  onUnlockSuccess: () => void;
  onEnsureSaved?: () => Promise<string | null>;
}

async function getToken(): Promise<string | null> {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token ?? null;
}

async function startCheckout(
  type: 'one_time' | 'subscription',
  projectId: string | null,
  setLoading: (v: boolean) => void,
  setError: (v: string | null) => void
) {
  setLoading(true);
  setError(null);
  try {
    const token = await getToken();
    if (!token) throw new Error('Not authenticated');

    const res = await fetch('/.netlify/functions/create-checkout-session', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ project_id: projectId, type }),
    });

    const data = await res.json();
    if (!res.ok || !data.url) throw new Error(data.error || 'Failed to start checkout');

    window.location.href = data.url;
  } catch (err) {
    setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    setLoading(false);
  }
}

function NoDataLockScreen() {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 sm:p-12 text-center max-w-lg mx-auto">
      <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-5">
        <Lock className="w-7 h-7 text-gray-400" />
      </div>
      <h2 className="text-xl font-semibold text-gray-900 mb-3">Results Locked</h2>
      <p className="text-gray-600 text-sm leading-relaxed">
        Establish the existing load on the <strong>Existing Load</strong> tab to unlock your
        results: upload the home&apos;s utility interval data (Measured demand), or enter the
        floor area and appliance inventory (Traditional calculation).
      </p>
    </div>
  );
}

// The "know what you're buying" line above the unlock controls. Measured:
// the data was processed. Traditional: the profile is computable, plus the
// attestation state and defaults count the report will disclose.
function DataVerifiedBadge({ readiness }: { readiness: LockScreenReadiness }) {
  return (
    <div
      className="flex flex-col items-center justify-center gap-1 mb-6"
      data-testid="lock-screen-readiness"
      data-path={readiness.path ?? ''}
    >
      <div className="flex items-center justify-center gap-2">
        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
        <span className="text-sm text-gray-500 font-medium">{readiness.headline}</span>
      </div>
      {readiness.detail && <span className="text-xs text-gray-500">{readiness.detail}</span>}
    </div>
  );
}

function formatCardBrand(brand: string | null): string {
  if (!brand) return 'Card';
  return brand.charAt(0).toUpperCase() + brand.slice(1);
}

export function ResultsLockScreen({
  project,
  amiData,
  equipment = [],
  projectId,
  paymentStatus,
  onUnlockSuccess,
  onEnsureSaved,
}: ResultsLockScreenProps) {
  const [unlockLoading, setUnlockLoading] = useState(false);
  const [subLoading, setSubLoading] = useState(false);
  const [trialLoading, setTrialLoading] = useState(false);
  const [savingBeforeCharge, setSavingBeforeCharge] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingConsent, setPendingConsent] = useState<null | 'one_time' | 'subscription'>(null);

  // Either existing-load path can carry a project to the paywall: interval
  // data or a manual peak (measured), or a computable dwelling profile
  // (traditional — floor area entered and inside the section's scope). Until
  // 2026-09-02 only the measured path counted, so a contractor without a
  // subscription could never unlock a traditional-only project: this screen
  // asked for utility data the method does not need and offered no trial,
  // unlock or subscribe control. The decision lives in lockScreenReadiness.ts
  // so it is unit tested without a render.
  const readiness = lockScreenReadiness(project, amiData, equipment);
  const hasData = readiness.hasData;

  if (!hasData) {
    return <NoDataLockScreen />;
  }

  const isFirmMemberWithInactivePlan =
    !!paymentStatus.firm_subscription_status &&
    paymentStatus.firm_subscription_status !== 'active' &&
    paymentStatus.subscription_status !== 'active' &&
    !project.results_unlocked;

  if (isFirmMemberWithInactivePlan) {
    return (
      <div
        data-testid="firm-inactive-lock-screen"
        className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sm:p-10 max-w-lg mx-auto"
      >
        <div className="text-center">
          <div className="w-14 h-14 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <Building2 className="w-7 h-7 text-amber-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Firm Plan Inactive</h2>
          <p className="text-sm text-gray-600 leading-relaxed">
            Your firm's TrueLoad subscription is not currently active. Contact your firm admin to restore access.
          </p>
        </div>
      </div>
    );
  }

  // Active firm, but this member holds no seat. Distinct from the branch above
  // — nothing is wrong with the firm's billing, and distinct from the generic
  // paywall below, which would invite someone to buy personally what their firm
  // has already paid for. The fix is a seat from their admin, so say that.
  const isFirmMemberWithoutSeat =
    paymentStatus.firm_subscription_status === 'active' &&
    !paymentStatus.firm_has_seat &&
    paymentStatus.subscription_status !== 'active' &&
    !project.results_unlocked;

  if (isFirmMemberWithoutSeat) {
    return (
      <div
        data-testid="firm-no-seat-lock-screen"
        className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sm:p-10 max-w-lg mx-auto"
      >
        <div className="text-center">
          <div className="w-14 h-14 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <Building2 className="w-7 h-7 text-blue-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">You don't have a seat yet</h2>
          <p className="text-sm text-gray-600 leading-relaxed">
            Your firm's TrueLoad plan is active, but running calculations needs one of its
            seats. Ask a firm admin to assign you one from the firm dashboard — there's
            nothing to buy here.
          </p>
          <p className="mt-4 text-xs text-gray-500">
            Everything except Results stays available in the meantime, so you can keep
            setting up projects and entering data.
          </p>
        </div>
      </div>
    );
  }

  const isTrialEligible =
    !paymentStatus.trial_used &&
    paymentStatus.subscription_status !== 'active' &&
    !paymentStatus.firm_subscription_status &&
    !project.results_unlocked;

  async function handleRedeemTrial() {
    setTrialLoading(true);
    setCheckoutError(null);
    try {
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');
      const res = await fetch('/.netlify/functions/redeem-trial', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ project_id: projectId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to redeem trial');
      onUnlockSuccess();
    } catch (err) {
      setCheckoutError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setTrialLoading(false);
    }
  }

  if (isTrialEligible) {
    const isLocal = !projectId || projectId.startsWith('local_');
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sm:p-10 max-w-lg mx-auto">
        <div className="text-center mb-6">
          <div className="w-14 h-14 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-7 h-7 text-green-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Your first project is on us</h2>
          <p className="text-sm text-gray-600 leading-relaxed">
            {isLocal
              ? 'Save this project first to unlock your free trial.'
              : 'Unlock this project for free — no card required. Future projects are $30 each, or $59/month for unlimited.'}
          </p>
        </div>

        <DataVerifiedBadge readiness={readiness} />

        {checkoutError && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700">{checkoutError}</p>
          </div>
        )}

        {!isLocal && (
          <div className="space-y-3">
            <button
              data-testid="btn-redeem-trial"
              onClick={handleRedeemTrial}
              disabled={trialLoading}
              className="w-full py-3 px-4 bg-green-600 text-white rounded-lg font-semibold text-sm hover:bg-green-700 transition-colors disabled:bg-green-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {trialLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  Processing...
                </>
              ) : (
                'Unlock Free — First Project On Us'
              )}
            </button>
            <p className="text-center text-xs text-gray-500 italic pt-1">
              Take TrueLoad for a no-risk test drive. Generate your first report, free.
            </p>
          </div>
        )}
      </div>
    );
  }

  const { has_saved_payment_method, card_last4, card_brand } = paymentStatus;

  const primaryButtonLabel = has_saved_payment_method
    ? `Unlock for $30 — charge to ${formatCardBrand(card_brand)} ••••${card_last4}`
    : 'Unlock Project — $30';

  const secondaryLinkLabel = has_saved_payment_method
    ? 'Switch to unlimited — $59/month, save after 2 projects'
    : 'Subscribe for $59/month — unlimited projects';

  // ---------------------------------------------------------------------------
  // LOCKED INVARIANT: never open a payment path on an unsaved project.
  // A newly created project lives under a temporary "local_" id until the first
  // explicit Save or the 30s autosave tick promotes it to a Supabase UUID.
  // Charging against a local_ id took the customer's money and then failed to
  // unlock anything. Promote first; only then let money move. If promotion
  // fails (offline, Supabase down), surface an actionable error and charge nothing.
  // ---------------------------------------------------------------------------
  async function ensureSavedForPayment(): Promise<string | null> {
    const currentIsLocal = !projectId || projectId.startsWith('local_');
    if (!currentIsLocal) return projectId;

    if (!onEnsureSaved) {
      setCheckoutError('This project has not been saved yet. Press Save, then try again.');
      return null;
    }

    setSavingBeforeCharge(true);
    setCheckoutError(null);
    try {
      const savedId = await onEnsureSaved();
      if (!savedId || savedId.startsWith('local_')) {
        setCheckoutError('We could not save this project to the cloud, so we have not charged you. Check your connection, press Save, and try again.');
        return null;
      }
      return savedId;
    } catch {
      setCheckoutError('We could not save this project to the cloud, so we have not charged you. Check your connection, press Save, and try again.');
      return null;
    } finally {
      setSavingBeforeCharge(false);
    }
  }

  async function handlePrimaryClick() {
    const savedId = await ensureSavedForPayment();
    if (!savedId) return;

    if (has_saved_payment_method) {
      setShowConfirmModal(true);
    } else {
      setPendingConsent('one_time');
    }
  }

  async function handleSecondaryClick(e: React.MouseEvent) {
    e.preventDefault();
    const savedId = await ensureSavedForPayment();
    if (!savedId) return;
    setPendingConsent('subscription');
  }

  const paidButtonsDisabled = unlockLoading || subLoading || savingBeforeCharge;

  return (
    <>
      <div data-testid="lock-screen" className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sm:p-10 max-w-lg mx-auto">
        <div className="text-center mb-6">
          <div className="w-14 h-14 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-7 h-7 text-blue-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Results Locked</h2>
          <p className="text-sm text-gray-600 leading-relaxed">
            Unlock this project to view your PASS/FAIL result and generate your AHJ-ready report.
          </p>
        </div>

        <DataVerifiedBadge readiness={readiness} />

        {checkoutError && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700">{checkoutError}</p>
          </div>
        )}

        <div className="space-y-3">
          <button
            data-testid="btn-unlock-pay-per-use"
            onClick={handlePrimaryClick}
            disabled={paidButtonsDisabled}
            className="w-full py-3 px-4 bg-blue-600 text-white rounded-lg font-semibold text-sm hover:bg-blue-700 transition-colors disabled:bg-blue-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {savingBeforeCharge ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                Saving project...
              </>
            ) : unlockLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                Redirecting to checkout...
              </>
            ) : (
              primaryButtonLabel
            )}
          </button>

          <div className="text-center">
            <button
              data-testid="btn-unlock-subscribe"
              onClick={handleSecondaryClick}
              disabled={paidButtonsDisabled}
              className="text-sm text-blue-600 hover:text-blue-800 underline underline-offset-2 disabled:text-gray-400 disabled:cursor-not-allowed inline-flex items-center gap-1"
            >
              {subLoading ? (
                <>
                  <div className="animate-spin rounded-full h-3 w-3 border-2 border-blue-600 border-t-transparent" />
                  Redirecting...
                </>
              ) : (
                secondaryLinkLabel
              )}
            </button>
          </div>

          <p className="text-center text-xs text-gray-400 pt-1">
            Subscriptions save money after 2 projects/month. Cancel anytime from account settings.
          </p>
        </div>
      </div>

      {pendingConsent && (
        <BillingConsentModal
          kind={pendingConsent}
          planLabel={pendingConsent === 'subscription' ? 'Unlimited' : 'Unlock this project'}
          amountText={pendingConsent === 'subscription' ? '$59.00' : '$30.00'}
          intervalText={pendingConsent === 'subscription' ? 'month' : undefined}
          onConfirm={() => {
            const type = pendingConsent;
            setPendingConsent(null);
            if (type === 'subscription') {
              startCheckout('subscription', projectId, setSubLoading, setCheckoutError);
            } else {
              startCheckout('one_time', projectId, setUnlockLoading, setCheckoutError);
            }
          }}
          onCancel={() => setPendingConsent(null)}
        />
      )}

      {showConfirmModal && projectId && !projectId.startsWith('local_') && (
        <ChargeConfirmModal
          projectId={projectId}
          cardBrand={card_brand}
          cardLast4={card_last4}
          onSuccess={() => {
            setShowConfirmModal(false);
            onUnlockSuccess();
          }}
          onCancel={() => setShowConfirmModal(false)}
        />
      )}
    </>
  );
}
